/**
 * 🔐 LOGIN CARD - Carte de connexion spatiale
 * Formulaire de connexion par téléphone ou email avec style spatial
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const LoginCard = ({
  // Login method
  loginMethod,
  setLoginMethod,
  
  // Phone auth
  phone,
  setPhone,
  hasStoredPhone,
  setHasStoredPhone,
  pin,
  pinRefs,
  handlePinInput,
  handleKeyDown,
  handleLogin,
  
  // Email auth
  email,
  setEmail,
  emailCode,
  emailCodeRefs,
  emailCodeSent,
  emailCooldown,
  handleSendEmailCode,
  handleEmailCodeInput,
  handleVerifyEmailCode,
  resetEmailForm,
  
  // State
  isLoading
}) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2 }}
      className="relative backdrop-blur-xl rounded-3xl p-6 md:p-8 overflow-hidden"
      style={{
        background: 'rgba(10, 0, 30, 0.85)',
        border: '2px solid rgba(0, 255, 255, 0.5)',
        boxShadow: '0 0 50px rgba(0, 255, 255, 0.3), 0 25px 80px rgba(255, 0, 255, 0.2)'
      }}
    >
      {/* Effet de lumière qui parcourt les bords */}
      <motion.div
        className="absolute w-20 h-20 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.8) 0%, transparent 70%)',
          filter: 'blur(10px)',
        }}
        animate={{
          left: ['0%', '100%', '100%', '0%', '0%'],
          top: ['0%', '0%', '100%', '100%', '0%'],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      {/* Onglets de connexion */}
      <div className="flex mb-6 bg-black/30 rounded-xl p-1">
        <button
          type="button"
          onClick={() => { setLoginMethod('phone'); resetEmailForm(); }}
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
            loginMethod === 'phone'
              ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-lg'
              : 'text-gray-400 hover:text-white'
          }`}
          data-testid="login-tab-phone"
        >
          <Phone className="w-4 h-4" />
          Par téléphone
        </button>
        <button
          type="button"
          onClick={() => { setLoginMethod('email'); }}
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
            loginMethod === 'email'
              ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-lg'
              : 'text-gray-400 hover:text-white'
          }`}
          data-testid="login-tab-email"
        >
          <Send className="w-4 h-4" />
          Par email
        </button>
      </div>

      {/* Formulaire Téléphone + PIN */}
      {loginMethod === 'phone' && (
        <form onSubmit={handleLogin}>
          {/* Affichage du numéro mémorisé ou champ de saisie */}
          {hasStoredPhone && phone ? (
            <div className="mb-6">
              <div className="flex items-center justify-center gap-3 p-4 rounded-xl bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/50">
                <Phone className="w-5 h-5 text-cyan-400" />
                <span className="text-white text-lg font-bold">
                  {phone.slice(0, 2)}••••••{phone.slice(-2)}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setHasStoredPhone(false);
                    setPhone('');
                  }}
                  className="text-xs text-gray-400 hover:text-white underline ml-2"
                >
                  Changer
                </button>
              </div>
              <p className="text-center text-gray-400 text-sm mt-2">
                🔒 Numéro mémorisé • Tapez votre PIN
              </p>
            </div>
          ) : (
            <div className="mb-6">
              <label className="block text-white text-sm font-semibold mb-2">
                📱 Ton numéro de téléphone
              </label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="06 12 34 56 78"
                className="w-full text-center text-lg font-bold py-4"
                style={{
                  background: 'rgba(0, 255, 255, 0.1)',
                  border: '2px solid rgba(0, 255, 255, 0.5)',
                  color: '#FFFFFF',
                }}
                data-testid="phone-input"
              />
            </div>
          )}

          {/* PIN à 4 chiffres */}
          <div className="mb-6">
            <label className="block text-white text-sm font-semibold mb-3 text-center">
              🔐 Code PIN (4 chiffres)
            </label>
            <div className="flex justify-center gap-3">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  id={`client-pin-${index}`}
                  ref={(el) => pinRefs.current[index] = el}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    handlePinInput(index, val);
                  }}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-14 h-14 text-center text-2xl font-bold rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all"
                  style={{
                    background: digit ? 'rgba(16, 185, 129, 0.3)' : 'rgba(0, 255, 255, 0.1)',
                    border: digit ? '2px solid #10B981' : '2px solid rgba(0, 255, 255, 0.5)',
                    color: '#FFFFFF',
                    boxShadow: digit ? '0 0 15px rgba(16, 185, 129, 0.5)' : 'none',
                  }}
                  data-testid={`pin-input-${index}`}
                />
              ))}
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading || pin.some(d => d === '')}
            className="w-full py-5 text-lg font-bold"
            style={{
              background: 'linear-gradient(135deg, #00FFFF 0%, #FF00FF 100%)',
              boxShadow: '0 0 30px rgba(0, 255, 255, 0.5)'
            }}
            data-testid="login-submit-button"
          >
            {isLoading ? '🚀 Connexion...' : '🚀 Embarquer !'}
          </Button>
        </form>
      )}

      {/* Formulaire Email */}
      {loginMethod === 'email' && (
        <div>
          {!emailCodeSent ? (
            // Étape 1: Saisie de l'email
            <form onSubmit={handleSendEmailCode}>
              <div className="mb-6">
                <label className="block text-white text-sm font-semibold mb-2">
                  📧 Ton adresse email
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ton@email.com"
                  className="w-full text-center text-lg font-bold py-4"
                  style={{
                    background: 'rgba(0, 255, 255, 0.1)',
                    border: '2px solid rgba(0, 255, 255, 0.5)',
                    color: '#FFFFFF',
                  }}
                  data-testid="email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading || !email || !email.includes('@')}
                className="w-full py-5 text-lg font-bold"
                style={{
                  background: 'linear-gradient(135deg, #00FFFF 0%, #FF00FF 100%)',
                  boxShadow: '0 0 30px rgba(0, 255, 255, 0.5)'
                }}
                data-testid="send-email-code-button"
              >
                {isLoading ? '📨 Envoi...' : '📨 Recevoir mon code'}
              </Button>
            </form>
          ) : (
            // Étape 2: Saisie du code reçu par email
            <div>
              <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/50">
                <p className="text-white text-center">
                  📧 Code envoyé à <span className="font-bold text-cyan-400">{email}</span>
                </p>
              </div>

              <form onSubmit={handleVerifyEmailCode}>
                <div className="mb-6">
                  <label className="block text-white text-sm font-semibold mb-3 text-center">
                    🔐 Code à 6 chiffres
                  </label>
                  <div className="flex justify-center gap-2">
                    {emailCode.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => emailCodeRefs.current[index] = el}
                        type="password"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          handleEmailCodeInput(index, val);
                        }}
                        className="w-11 h-14 text-center text-xl font-bold rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all"
                        style={{
                          background: digit ? 'rgba(16, 185, 129, 0.3)' : 'rgba(0, 255, 255, 0.1)',
                          border: digit ? '2px solid #10B981' : '2px solid rgba(0, 255, 255, 0.5)',
                          color: '#FFFFFF',
                          boxShadow: digit ? '0 0 15px rgba(16, 185, 129, 0.5)' : 'none',
                        }}
                        data-testid={`email-code-input-${index}`}
                      />
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || emailCode.some(d => d === '')}
                  className="w-full py-5 text-lg font-bold mb-3"
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #00FFFF 100%)',
                    boxShadow: '0 0 30px rgba(16, 185, 129, 0.5)'
                  }}
                  data-testid="verify-email-code-button"
                >
                  {isLoading ? 'Vérification...' : 'Vérifier le code'}
                </Button>
              </form>

              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendEmailCode}
                  disabled={isLoading || emailCooldown > 0}
                  className="w-full text-sm"
                  style={{
                    borderColor: '#00FFFF',
                    color: emailCooldown > 0 ? '#666' : '#00FFFF'
                  }}
                >
                  {emailCooldown > 0 ? `Renvoyer le code (${emailCooldown}s)` : '📧 Renvoyer le code'}
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetEmailForm}
                  className="w-full text-gray-400 text-sm"
                >
                  ← Changer d'email
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default LoginCard;
